import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { theme } from "../../Theme";
import PlannerGrid from "./components/PlannerGrid";
import {
  setPlannerEvents,
  setPlannerGridData,
  setPlannerLoading,
  setPlannerError,
  setCurrentScenarioId,
  markCellModified,
  setCellLoading,
  updatePlannerCell,
  setPlanId,
} from "../../Redux/promoSimulatorSlice/promoSimulatorSlice";
import { promoSimulatorService } from "./services/promoSimulatorService";
import { promoService } from "../../features/promoSimulator/services/promoService";

export default function PlannerCalendar() {
  const dispatch = useDispatch();
  const {
    plannerEvents,
    plannerInitialized,
    plannerGridData,
    scenarios,
    currentScenarioId,
    modifiedCells,
    cellLoading,
    plannerLoading,
    plannerError,
    planId,
  } = useSelector(
    (state) => state.promoSimulator
  );
  const projectId = useSelector((state) => state.userProject.projectId);
  const [loading, setLoading] = useState(false);
  const [startWeek, setStartWeek] = useState(null);
  const [endWeek, setEndWeek] = useState(null);
  const [recommendedPlan, setRecommendedPlan] = useState(null);

  const recommendedSnapshotRef = useRef(null);
  const recommendedWeeksRef = useRef({ start: null, end: null });

  const recommendedScenarioId = recommendedPlan?.id ?? "recommended";

  const buildPlannerGridFromSavedScenario = (scenario) => {
    const id = scenario?.id ?? `scenario_${Date.now()}`;
    const name = scenario?.name ?? "Saved Scenario";
    const discountPct =
      typeof scenario?.discountPct === "number" && Number.isFinite(scenario.discountPct)
        ? scenario.discountPct
        : 0;

    const weekFromScenario =
      scenario?.weeks?.startWeek ??
      scenario?.weeks?.week ??
      scenario?.kpis?.week ??
      null;
    const endWeekFromScenario =
      scenario?.weeks?.endWeek ??
      scenario?.weeks?.week ??
      scenario?.kpis?.week ??
      null;
    const weekNum = Number.isFinite(Number(weekFromScenario))
      ? Number(weekFromScenario)
      : 1;
    const endWeekNum = Number.isFinite(Number(endWeekFromScenario))
      ? Number(endWeekFromScenario)
      : weekNum;
    const safeStart = Math.max(1, Math.min(52, weekNum));
    const safeEnd = Math.max(safeStart, Math.min(52, endWeekNum));
    const weeks = Array.from(
      { length: safeEnd - safeStart + 1 },
      (_, i) => safeStart + i
    );

    const skuLabels = Array.isArray(scenario?.skus) ? scenario.skus : [];
    const skus = skuLabels.length
      ? skuLabels.map((label, idx) => ({
          skuId: `${String(id)}_sku_${idx}`,
          skuLabel: String(label),
        }))
      : [{ skuId: `${String(id)}_sku_0`, skuLabel: "Selected SKU" }];

    const rows = skus.map((sku) => ({
      skuId: sku.skuId,
      skuLabel: sku.skuLabel,
      brand: scenario?.brand ?? scenario?.kpis?.brand,
      manufacturer: scenario?.manufacturer ?? scenario?.kpis?.manufacturer,
      cells: {
        ...weeks.reduce((acc, w) => {
          acc[w] = {
            skuId: sku.skuId,
            week: w,
            discountPct,
            kpis: { ...(scenario?.kpis || {}) },
          };
          return acc;
        }, {}),
      },
    }));

    return {
      id,
      name,
      weeks,
      rows,
      modifiedCells: {},
    };
  };

  useEffect(() => {
    if (!plannerInitialized) {
      setLoading(true);
      promoSimulatorService.getPlannerEvents().then((events) => {
        dispatch(setPlannerEvents(events));
        setLoading(false);
      });
    }
  }, [plannerInitialized, dispatch]);

  useEffect(() => {
    async function bootstrapPlanner() {
      dispatch(setPlannerLoading(true));
      dispatch(setPlannerError(null));
      try {
        const baseline = await promoService.getRecommendedPlan();
        setRecommendedPlan(baseline);

        dispatch(setPlannerGridData(baseline)); // also clears plannerLoading in reducer
      } catch {
        dispatch(setPlannerError("Failed to load planner scenarios."));
        dispatch(setPlannerLoading(false));
      }
    }

    if (!plannerGridData && !plannerError) {
      bootstrapPlanner();
    }

    return () => {
      dispatch(setPlannerLoading(false));
    };
  }, [dispatch, plannerGridData, plannerError]);

  useEffect(() => {
    if (!plannerGridData?.weeks?.length) return;
    const sorted = [...plannerGridData.weeks].sort((a, b) => a - b);
    const w0 = sorted[0];
    const wLast = sorted[sorted.length - 1];
    const isRecommended =
      String(plannerGridData?.id) === String(recommendedScenarioId);
    if (isRecommended) {
      const fromOptimizeApi = plannerGridData?.source === "optimize_api";
      if (!fromOptimizeApi) {
        setStartWeek(null);
        setEndWeek(null);
        return;
      }
      setStartWeek(w0);
      setEndWeek(wLast);
      return;
    }
    setStartWeek(w0);
    setEndWeek(wLast);
  }, [
    plannerGridData?.id,
    plannerGridData?.weeks,
    plannerGridData?.source,
    recommendedScenarioId,
  ]);

  useEffect(() => {
    if (
      plannerGridData &&
      String(plannerGridData.id) === String(recommendedScenarioId) &&
      plannerGridData.source === "optimize_api"
    ) {
      recommendedSnapshotRef.current = plannerGridData;
    }
  }, [plannerGridData, recommendedScenarioId]);

  useEffect(() => {
    if (
      plannerGridData &&
      String(plannerGridData.id) === String(recommendedScenarioId) &&
      plannerGridData.source === "optimize_api" &&
      startWeek != null &&
      endWeek != null
    ) {
      recommendedWeeksRef.current = { start: startWeek, end: endWeek };
    }
  }, [plannerGridData, recommendedScenarioId, startWeek, endWeek]);

  const scenarioOptions = useMemo(() => {
    const recommendedId = recommendedScenarioId;
    const recommendedName = recommendedPlan?.name || "Recommended Baseline";

    const saved =
      (scenarios || []).map((s) => ({
        id: s.id,
        name: s.name || `Scenario ${s.id}`,
      })) || [];

    const withoutRecommended = saved.filter(
      (s) => String(s.id) !== String(recommendedId)
    );

    return [{ id: recommendedId, name: recommendedName }, ...withoutRecommended];
  }, [scenarios, plannerGridData, recommendedPlan, recommendedScenarioId]);

  const handleScenarioChange = async (id) => {
    dispatch(setCurrentScenarioId(id));
    dispatch(setPlannerLoading(true));
    dispatch(setPlannerError(null));
    try {
      const selected = scenarios.find((s) => String(s.id) === String(id));
      if (selected) {
        dispatch(setPlannerGridData(buildPlannerGridFromSavedScenario(selected)));
        return;
      }
      const restored = recommendedSnapshotRef.current;
      if (restored && String(id) === String(recommendedScenarioId)) {
        dispatch(setPlannerGridData(restored));
        const rw = recommendedWeeksRef.current;
        if (rw.start != null && rw.end != null) {
          setStartWeek(rw.start);
          setEndWeek(rw.end);
        }
        return;
      }
      const baseline =
        recommendedPlan ?? (await promoService.getRecommendedPlan());
      if (!recommendedPlan) {
        setRecommendedPlan(baseline);
      }
      dispatch(setPlannerGridData(baseline));
    } catch {
      dispatch(setPlannerError("Failed to load selected scenario."));
      dispatch(setPlannerLoading(false));
    }
  };

  const handlePlannerReset = async () => {
    recommendedSnapshotRef.current = null;
    recommendedWeeksRef.current = { start: null, end: null };
    dispatch(setPlannerError(null));
    dispatch(setPlanId(null));
    dispatch(setPlannerLoading(true));
    try {
      const baseline =
        recommendedPlan ?? (await promoService.getRecommendedPlan());
      if (!recommendedPlan) {
        setRecommendedPlan(baseline);
      }
      dispatch(setPlannerGridData(baseline));
      setStartWeek(null);
      setEndWeek(null);
    } catch {
      dispatch(setPlannerError("Failed to reset planner."));
      dispatch(setPlannerLoading(false));
    }
  };

  const handleCellChange = async ({ skuId, week, patch }) => {
    if (week == null || !Number.isFinite(Number(week))) {
      dispatch(setPlannerError("Select start and end week, then Plan, before editing."));
      return;
    }

    // Optimistic update so the chip changes immediately
    dispatch(markCellModified({ skuId, week, field: "discountPct" }));
    dispatch(updatePlannerCell({ skuId, week, patch }));

    // Resolve SKU name from the grid rows (used as the API key)
    const row = plannerGridData?.rows?.find((r) => r.skuId === skuId);
    const skuName = row?.skuLabel;
    // userEnteredPct is the raw % the user typed (e.g. 15); convert to decimal for API
    const enteredPct = patch.userEnteredPct ?? patch.discountPct;
    const selectedDiscount =
      enteredPct != null && Number.isFinite(Number(enteredPct))
        ? Number(enteredPct) / 100
        : null;

    if (!planId || !skuName || selectedDiscount == null) {
      // No active plan yet (mock/local scenario) – nothing to persist
      return;
    }

    dispatch(setCellLoading({ skuId, week, loading: true }));
    try {
      const res = await promoSimulatorService.updatePlannerCellApi({
        planId,
        projectId,
        skuName,
        week: Number(week),
        selectedDiscount,
      });

      // Replace KPIs from API but keep the % the user typed for display
      if (res?.cellPatch) {
        dispatch(
          updatePlannerCell({
            skuId,
            week,
            patch: {
              ...res.cellPatch,
              userEnteredPct: enteredPct,
              discountPct: enteredPct,
            },
          })
        );
      }
      // Refresh planId in case it changes (API returns it back)
      if (res?.planId && res.planId !== planId) {
        dispatch(setPlanId(res.planId));
      }
    } catch (e) {
      dispatch(setPlannerError(e?.message || "Failed to update cell."));
    } finally {
      dispatch(setCellLoading({ skuId, week, loading: false }));
    }
  };

  return (
    <Box>
      <Box mb={2}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color={theme.palette.primary.main}
        >
          Promo Planner
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Weekly promotional activity across all brands
        </Typography>
      </Box>

      {plannerError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {plannerError}
        </Alert>
      )}

      {plannerLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : plannerGridData ? (
        <PlannerGrid
          mode="sku"
          gridData={plannerGridData}
          modifiedCells={modifiedCells}
          cellLoading={cellLoading}
          onCellChange={handleCellChange}
          startWeek={startWeek}
          endWeek={endWeek}
          onStartWeekChange={setStartWeek}
          onEndWeekChange={setEndWeek}
          recommendedScenarioId={recommendedScenarioId}
          onRecommendedPlanApply={async ({
            startWeek: sw,
            endWeek: ew,
            manufacturer,
            brand,
            productClass,
          }) => {
            setStartWeek(sw);
            setEndWeek(ew);
            dispatch(setPlannerError(null));
            try {
              const result = await promoSimulatorService.optimizeAndBuildPlannerGrid({
                projectId,
                manufacturer,
                brand,
                productClass,
                start_week: sw,
                end_week: ew,
              });
              dispatch(setPlannerGridData(result));
              dispatch(setPlanId(result.planId ?? null));
            } catch (e) {
              dispatch(setPlannerError(e?.message || "Failed to load recommended plan."));
              throw e;
            }
          }}
          scenarioOptions={scenarioOptions}
          selectedScenarioId={currentScenarioId || plannerGridData?.id || ""}
          onScenarioChange={handleScenarioChange}
          scenarioDisabled={plannerLoading || scenarioOptions.length === 0}
          onPlannerReset={handlePlannerReset}
        />
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <PlannerGrid events={plannerEvents} />
      )}
    </Box>
  );
}
